import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import SkeletonBlock from '../components/ui/SkeletonBlock';
import StatusBanner from '../components/ui/StatusBanner';
import FocusQueueRow from '../components/today/FocusQueueRow';
import WorkspacePage from '../components/layout/WorkspacePage';
import {
  dismissFocusQueueItem,
  listFocusQueue,
  undismissFocusQueueItem
} from '../services/focusQueueApi';
import {
  applyFocusQueueCountDelta,
  emitFocusQueueChanged,
  subscribeToFocusQueueChanges
} from '../utils/focusQueueEvents';
import { DIRECTOR_DATA_UPDATED_EVENT } from '../utils/directorEvents';
import './TodayPage.css';

const DEFAULT_LIMIT = 15;
const VIEW_ALL_LIMIT = 200;

const FILTERS = [
  { key: '', label: 'All' },
  { key: 'approval', label: 'Approval' },
  { key: 'kpi', label: 'KPI' },
  { key: 'actuals', label: 'Actuals' },
  { key: 'attendance', label: 'Attendance' },
  { key: 'action-item', label: 'Action item' },
  { key: 'win', label: 'Win' }
];

function getCountForFilter(counts, filterKey) {
  if (!counts) return 0;
  if (!filterKey) return Number(counts.total || 0);
  return Number(counts.byType?.[filterKey] || 0);
}

function TodayPage({ onNavigate }) {
  const [activeType, setActiveType] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [dismissError, setDismissError] = useState('');
  const [dismissingKey, setDismissingKey] = useState('');
  const [lastDismissed, setLastDismissed] = useState(null);

  const limit = showAll ? VIEW_ALL_LIMIT : DEFAULT_LIMIT;

  const loadQueue = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const nextPayload = await listFocusQueue({
        type: activeType,
        limit
      });
      setPayload(nextPayload);
    } catch (loadError) {
      setError(loadError.message || 'Unable to load your focus queue.');
    } finally {
      setIsLoading(false);
    }
  }, [activeType, limit]);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    const handleDirectorDataUpdated = () => loadQueue();
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);
    return () => window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);
  }, [loadQueue]);

  useEffect(() => subscribeToFocusQueueChanges((event) => {
    const { action, item } = event.detail || {};
    const itemMatchesFilter = !activeType || item?.type === activeType;
    if (action === 'dismiss' && item?.itemKey) {
      if (!itemMatchesFilter) return;
      setPayload(current => current
        ? {
          ...current,
          items: (Array.isArray(current.items) ? current.items : []).filter(row => row.itemKey !== item.itemKey),
          counts: applyFocusQueueCountDelta(current.counts, item, -1)
        }
        : current);
      return;
    }
    if (action === 'restore' && item?.itemKey) {
      if (!itemMatchesFilter) return;
      setPayload(current => current
        ? {
          ...current,
          counts: applyFocusQueueCountDelta(current.counts, item, 1)
        }
        : current);
      return;
    }
    if (action === 'refresh') {
      loadQueue();
    }
  }), [activeType, loadQueue]);

  const items = Array.isArray(payload?.items) ? payload.items : [];
  const counts = payload?.counts || null;
  const totalForFilter = getCountForFilter(counts, activeType);
  const hasOverflow = !showAll && totalForFilter > DEFAULT_LIMIT;
  const generatedAt = payload?.generatedAt
    ? new Date(payload.generatedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : '';

  const activeFilterLabel = useMemo(
    () => FILTERS.find(filter => filter.key === activeType)?.label || 'All',
    [activeType]
  );

  const handleFilterChange = (nextType) => {
    setActiveType(nextType);
    setShowAll(false);
    setLastDismissed(null);
    setDismissError('');
  };

  const handleDismiss = async (item) => {
    if (!item?.itemKey) return;
    setDismissingKey(item.itemKey);
    setDismissError('');
    setLastDismissed(item);
    emitFocusQueueChanged({ action: 'dismiss', item });
    try {
      await dismissFocusQueueItem(item.itemKey);
      emitFocusQueueChanged({ action: 'refresh', item });
    } catch (dismissFailure) {
      setLastDismissed(null);
      emitFocusQueueChanged({ action: 'restore', item });
      setDismissError(dismissFailure.message || 'Unable to dismiss that item.');
    } finally {
      setDismissingKey('');
    }
  };

  const handleUndo = async () => {
    if (!lastDismissed?.itemKey) return;
    const restoredItem = lastDismissed;
    setDismissingKey(lastDismissed.itemKey);
    setDismissError('');
    setLastDismissed(null);
    emitFocusQueueChanged({ action: 'restore', item: restoredItem });
    try {
      await undismissFocusQueueItem(restoredItem.itemKey);
      emitFocusQueueChanged({ action: 'refresh', item: restoredItem });
    } catch (undoFailure) {
      setLastDismissed(restoredItem);
      emitFocusQueueChanged({ action: 'dismiss', item: restoredItem });
      setDismissError(undoFailure.message || 'Unable to restore that item.');
    } finally {
      setDismissingKey('');
    }
  };

  return (
    <WorkspacePage
      className="today-page"
      data-testid="workspace-today"
      data-workspace-id="today"
      eyebrow="Daily Focus Queue"
      title="Today"
      subtitle="Ranked work already waiting in approvals, KPIs, actuals, attendance, and action items."
      actions={(
        <div className="today-page__summary" aria-label="Queue summary">
          <span>Total</span>
          <strong>{counts?.total ?? '—'}</strong>
          <small>{counts?.overdue ? `${counts.overdue} overdue` : 'Nothing overdue'}</small>
        </div>
      )}
    >
      <section className="today-page__toolbar" aria-label="Focus queue filters">
        <div className="today-page__filters">
          {FILTERS.map(filter => (
            <Button
              key={filter.key || 'all'}
              size="sm"
              variant={activeType === filter.key ? 'primary' : 'secondary'}
              onClick={() => handleFilterChange(filter.key)}
              className="today-page__filter"
            >
              {filter.label}
            </Button>
          ))}
        </div>
        <div className="today-page__toolbar-meta">
          <span>{activeFilterLabel} · showing {items.length} of {totalForFilter}</span>
          {generatedAt ? <span>Updated {generatedAt}</span> : null}
        </div>
      </section>

      {error ? (
        <StatusBanner kind="error" message={error} onRetry={loadQueue} retryLabel="Try again" />
      ) : null}
      {dismissError ? <StatusBanner kind="error" message={dismissError} /> : null}
      {lastDismissed ? (
        <div className="today-page__undo-banner">
          <StatusBanner
            kind="success"
            message={`Dismissed "${lastDismissed.title}" for today.`}
          />
          <Button size="sm" variant="secondary" onClick={handleUndo} loading={dismissingKey === lastDismissed.itemKey}>
            Undo
          </Button>
        </div>
      ) : null}

      {isLoading ? (
        <div className="today-page__list" aria-label="Loading focus queue">
          {Array.from({ length: 5 }).map((_, index) => (
            <div className="today-page__skeleton-row" key={index}>
              <SkeletonBlock width={18} height={18} radius="var(--radius-pill)" />
              <div>
                <SkeletonBlock width="34%" height={12} />
                <SkeletonBlock width="78%" height={18} />
                <SkeletonBlock width="56%" height={12} />
              </div>
              <SkeletonBlock width={88} height={34} radius="var(--radius-pill)" />
            </div>
          ))}
        </div>
      ) : !error && items.length ? (
        <>
          <div className="today-page__list">
            {items.map(item => (
              <FocusQueueRow
                key={item.itemKey}
                item={item}
                onCta={(path) => onNavigate?.(path)}
                onDismiss={handleDismiss}
                dismissing={dismissingKey === item.itemKey}
              />
            ))}
          </div>
          {hasOverflow || showAll ? (
            <div className="today-page__view-all">
              <Button size="sm" variant="secondary" onClick={() => setShowAll(current => !current)}>
                {showAll ? 'Show first 15' : `View all ${totalForFilter}`}
              </Button>
            </div>
          ) : null}
        </>
      ) : !error ? (
        <EmptyState
          icon="✓"
          headline={activeType ? 'No items found' : 'Your queue is clear'}
          body={activeType ? 'No items found for this filter. Try All or refresh the queue.' : 'No action items in flight. Nothing needs your attention right now.'}
          compact
        />
      ) : null}
    </WorkspacePage>
  );
}

export default TodayPage;
